import { Fragment } from "react";

import { Check } from "lucide-react";

import type { Alternative } from "@/content/alternatives/schema";

interface CompetitorDetailSectionProps {
  alternative: Alternative;
  index: number;
  alternativeNumber: number;
}

export function CompetitorDetailSection({ alternative, index, alternativeNumber }: CompetitorDetailSectionProps) {
  const isGrayBg = index % 2 === 1;

  return (
    <section className={isGrayBg ? "bg-ash-100 py-16 md:py-24" : "bg-white py-16 md:py-24"}>
      <div className="mx-auto max-w-6xl px-6">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-clash text-3xl font-medium leading-tight tracking-snug text-ash-900 md:text-5xl">
            Alternative {alternativeNumber}: {alternative.name} - {alternative.tagline}
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-2">
          {alternative.detailFeatures && alternative.detailFeatures.length > 0 && (
            <div>
              <h3 className="mb-6 font-clash text-xl font-medium text-ash-900 md:text-2xl">
                Key Features &amp; Benefits
              </h3>
              <div className="space-y-5">
                {alternative.detailFeatures.map((feature) => (
                  <div key={feature.title} className="flex gap-3">
                    <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-rebolt-500/15">
                      <Check className="size-3.5 text-rebolt-500" strokeWidth={3} />
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-ash-900">{feature.title}</h4>
                      <p className="mt-1 text-sm leading-relaxed text-ash-500">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {alternative.pricingTable && (
            <div>
              <div className="rounded-2xl border border-ash-200 bg-white p-6">
                <h3 className="mb-4 font-clash text-xl font-medium text-ash-900">
                  <span className="text-ash-600">{alternative.name}</span> Pricing
                </h3>

                <div className="overflow-hidden rounded-xl border border-ash-200">
                  <div
                    className="grid gap-px bg-ash-200"
                    style={{
                      gridTemplateColumns: `1fr ${alternative.pricingTable.columns.map(() => "1fr").join(" ")}`,
                    }}
                  >
                    <div className="bg-ash-100 px-4 py-3 text-sm font-semibold text-ash-700">Plan</div>
                    {alternative.pricingTable.columns.map((col) => (
                      <div key={col} className="bg-ash-100 px-4 py-3 text-sm font-semibold text-ash-700">
                        {col}
                      </div>
                    ))}

                    {alternative.pricingTable.rows.map((row, ri) => (
                      <Fragment key={row.plan}>
                        <div
                          className={`px-4 py-3 text-sm font-medium text-ash-900 ${ri % 2 === 0 ? "bg-white" : "bg-ash-50"}`}
                        >
                          {row.plan}
                        </div>
                        {row.values.map((val, vi) => (
                          <div
                            key={`${row.plan}-${vi}`}
                            className={`px-4 py-3 text-sm text-ash-700 ${ri % 2 === 0 ? "bg-white" : "bg-ash-50"}`}
                          >
                            {val}
                          </div>
                        ))}
                      </Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {(alternative.strengths?.length || alternative.weaknesses?.length) && (
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
            {alternative.strengths && alternative.strengths.length > 0 && (
              <div className="rounded-2xl border border-ash-200 bg-white p-6">
                <h3 className="mb-5 font-clash text-xl font-medium text-ash-900">Strengths</h3>
                <div className="space-y-4">
                  {alternative.strengths.map((strength) => (
                    <div key={strength} className="flex items-start gap-3">
                      <div className="mt-2 size-2.5 shrink-0 rounded-full bg-rebolt-500" />
                      <p className="text-base leading-relaxed text-ash-700">{strength}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {alternative.weaknesses && alternative.weaknesses.length > 0 && (
              <div className="rounded-2xl border border-ash-200 bg-white p-6">
                <h3 className="mb-5 font-clash text-xl font-medium text-ash-900">Weaknesses</h3>
                <div className="space-y-4">
                  {alternative.weaknesses.map((weakness) => (
                    <div key={weakness} className="flex items-start gap-3">
                      <div className="mt-2 size-2.5 shrink-0 rounded-full bg-ash-400" />
                      <p className="text-base leading-relaxed text-ash-700">{weakness}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
